import { useEffect, useRef, useState, type ReactNode } from 'react'
import { emmaEngine } from '@/components/emma/Emma'

export interface ThinkLine {
  say: ReactNode
  res?: ReactNode
  ms?: number
  /** A "not here yet" line: a grey tick, not an amber one. */
  soft?: boolean
  on?: () => void
  after?: () => void
}

const reduced = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * Emma thinking out loud (Brian's think()): each line spins while she works, then ticks and shows what she found. She
 * thinks while the lines run and answers when they're done. Runs once per mount; key it to run again.
 */
export function Think({ lines, onDone }: { lines: ThinkLine[]; onDone?: () => void }) {
  const [shown, setShown] = useState(0)
  const [done, setDone] = useState(0)
  const ref = useRef({ lines, onDone })
  ref.current = { lines, onDone }

  useEffect(() => {
    let alive = true
    const rm = reduced()
    const wait = (ms: number) => new Promise((r) => window.setTimeout(r, rm ? Math.min(ms, 60) : ms))
    void (async () => {
      emmaEngine()?.sent()
      const L = ref.current.lines
      for (let i = 0; i < L.length; i++) {
        if (!alive) return
        setShown(i + 1)
        L[i].on?.()
        await wait(L[i].ms ?? 900)
        if (!alive) return
        setDone(i + 1)
        L[i].after?.()
        await wait(260)
      }
      if (!alive) return
      emmaEngine()?.answered()
      ref.current.onDone?.()
    })()
    return () => { alive = false }
  }, [])

  return (
    <div className="ob-think" aria-live="polite">
      {lines.slice(0, shown).map((l, i) => (
        <div key={i} className={`tl in${l.soft ? ' soft' : ''}${i < done ? ' done' : ''}`}>
          <span className="st" />
          <p>{l.say}{l.res != null && <span className="res">{l.res}</span>}</p>
        </div>
      ))}
    </div>
  )
}
